/**
 * Lobbero design tokens — the single source of truth for the product UI.
 *
 * Visual identity: Lagoon Teal, aligned to the lobbero_web marketing site so the
 * public site and the product read as one brand.
 * "The best doorman you've ever had, now with a memory that never forgets."
 *
 * These tokens are consumed directly by @emotion/styled template literals
 * (e.g. `background: ${palette.iris}`). Legacy `colors` / `font` objects in
 * colors.ts / text.tsx are kept as backwards-compatible aliases that now point
 * at this palette, so older components pick up the new look automatically.
 *
 * See design_system.md at the repo root for the full documented system.
 */

/* ─────────────────────────────  COLOR  ───────────────────────────── */

export const palette = {
  /* ── Brand — Lagoon Teal ──
   *
   * Aligned to the lobbero_web marketing site so the product and the public
   * site are one brand: --teal #00A19A, --lagoon #007C76, --ink #12292C,
   * --mint #F7FAF9, and the aurora sweep #00A19A → #43D6C9.
   *
   * The `iris*` KEY NAMES are retained deliberately. They are referenced at 283
   * call sites across the app, and remapping the values rather than renaming the
   * keys means every component adopts the new brand automatically with no
   * behavioural risk — the same technique this system already used when the
   * legacy `colors` object was repointed. Read `iris` as "the brand hue"; the
   * teal-named aliases below are the preferred spelling for new code.
   */
  iris: '#00A19A', // Primary — buttons, links, brand moments
  irisDark: '#007C76', // Lagoon — hover states, emphasis
  irisDeep: '#005E59', // Pressed / active, deepest brand ink
  irisGlow: '#43D6C9', // Aurora end + light accent
  irisTint: '#E4F5F3', // Soft brand fill (selected rows, chips, avatars)
  irisTintSoft: '#F1FAF9', // Softest brand wash (hover rows, subtle panels)

  /* Preferred names for new code — same values as the brand tokens above. */
  teal: '#00A19A',
  lagoon: '#007C76',
  tealDeep: '#005E59',
  tealGlow: '#43D6C9',
  tealTint: '#E4F5F3',
  tealTintSoft: '#F1FAF9',

  /* Ink & neutrals — Deep Pine family, tuned to the teal brand */
  ink: '#12292C', // Headlines & body text (lobbero_web --ink)
  inkSoft: '#2C4448', // Secondary headings
  /* Muted body / secondary text. Was #5E7477, which measured 4.49:1 on
     `surfaceMuted` — under the floor by a hundredth, on the inset panels where
     secondary text is densest. Darkened until it clears on all three surfaces
     with margin: 5.87:1 / 5.59:1 / 5.32:1. */
  slate: '#55686B',
  /* CAPTIONS AND PLACEHOLDERS ARE TEXT, SO THIS TOKEN IS MEASURED AS TEXT.
   *
   * It was #93A6A8: 2.54:1 on white, 2.42:1 on porcelain, 2.30:1 on
   * surfaceMuted — a long way under the 4.5:1 floor, on the token that carries
   * every placeholder, every caption, every metadata line and every chart axis
   * label in the product (129 call sites).
   *
   * There is no lighter value that passes. The ramp was walked: the lightest
   * teal-grey clearing 4.5:1 on ALL THREE surfaces is this one, and it lands
   * next to `slate`. That is the honest answer rather than a disappointing one —
   * a third text tier cannot be built out of contrast below the floor. Where a
   * caption needs to recede, it recedes by SIZE and WEIGHT, which this palette
   * already provides and which cost nobody their ability to read it.
   *
   * 5.02:1 on surface, 4.78:1 on porcelain, 4.56:1 on surfaceMuted. */
  slateLight: '#5D7376', // Placeholder / captions / metadata
  /* The OLD slateLight, kept for ONE job: text inside a genuinely disabled
   * control. WCAG 1.4.3 exempts those, and a disabled field that still looks
   * live is its own usability bug. Never use it for a caption or a placeholder —
   * a placeholder is live text in an enabled field. */
  slateDisabled: '#93A6A8',
  line: '#DCE8E7', // Hairline borders on mint
  lineStrong: '#C6D8D6', // Stronger dividers / resting control borders

  /* Surfaces */
  porcelain: '#F7FAF9', // App background — lobbero_web --mint
  surface: '#FFFFFF', // Cards, inputs, sheets
  surfaceMuted: '#EFF5F4', // Subtle inset panels on mint
  overlay: 'rgba(18, 41, 44, 0.48)', // Modal / drawer scrim (ink-based)
  surfaceGlass: 'rgba(255, 255, 255, 0.82)', // Frosted navigation surfaces
  tealWash: 'rgba(0, 161, 154, 0.16)', // Ambient brand wash
  tealWashSoft: 'rgba(0, 161, 154, 0.12)', // Quiet ambient brand wash
  tealTransparent: 'rgba(0, 161, 154, 0)', // Gradient fade stop
  onBrandStrong: 'rgba(255, 255, 255, 0.94)',
  onBrandMuted: 'rgba(255, 255, 255, 0.72)',
  onBrandSubtle: 'rgba(255, 255, 255, 0.1)',

  /* Semantic — amber and coral already matched lobbero_web exactly. */
  success: '#4C9F38', // Leaf (lobbero_web --leaf) — confirmations, "checked in"
  successSoft: '#EAF4E6',
  warning: '#F59E0B', // Amber — pending approvals
  warningSoft: '#FDF1DE',
  alert: '#F0564A', // Coral — incidents, emergencies. Borders, dots, icons (3:1).
  // Coral, darkened until white clears 4.5:1 (5.32:1). This is the FILL under a
  // white label on a destructive button — #F0564A is only 3.42:1, so the one
  // control whose consequence is irreversible had the hardest label to read.
  // Not a second brand colour: never use it for a border, a dot or an icon.
  alertStrong: '#C3392E',
  // Pressed / hover state under alertStrong (7.06:1). A destructive hover must
  // go DARKER — a hover that lightens reads as the control going inactive.
  alertDeep: '#A32E25',
  alertSoft: '#FCE9E7',

  white: '#FFFFFF',
  black: '#12292C', // "Black" resolves to ink, never a harsh pure black
} as const;

/**
 * Status pill tints — { bg, fg, dot } triples for badges (roles, approval
 * states, priority, incidents). Text colour always meets contrast on its tint.
 */
export const status = {
  brand: { bg: '#E4F5F3', fg: '#00615C', dot: '#00A19A' },
  neutral: { bg: '#EFF2F2', fg: '#2C4448', dot: '#5E7477' },
  success: { bg: '#EAF4E6', fg: '#356E27', dot: '#4C9F38' },
  warning: { bg: '#FDF1DE', fg: '#9A6207', dot: '#F59E0B' },
  alert: { bg: '#FCE9E7', fg: '#B23127', dot: '#F0564A' },
} as const;

/**
 * Data-viz palette — teal-anchored, restrained, and every value below is a
 * MEASURED one rather than a chosen one.
 *
 * Guarded by `src/style-utils/dataviz-palette.test.ts`, which recomputes OKLab
 * lightness, OKLab chroma, dichromat ΔE and WCAG contrast inside the repo and
 * fails if any of them drifts. The ORDER of `series` is part of what was
 * validated, not a preference: reordering it invalidates the separation numbers
 * quoted below. Run that test before adding, removing or "tidying" a value here.
 *
 * ── WHAT THE PREVIOUS SCALE COST ──
 * The scale this replaces was
 * ['#00A19A', '#43D6C9', '#4C9F38', '#F59E0B', '#F0564A', '#005E59']
 * and it failed four separate things:
 *
 *  · #43D6C9 (the aurora end) sat at OKLab L 0.797 — outside the 0.43–0.77
 *    usable band — and at 1.79:1 against `palette.surface`. As a 2px line on a
 *    white card it was invisible: the series was drawn and could not be seen.
 *  · #005E59 (tealDeep) had chroma 0.075, under the 0.10 floor at which a hue
 *    stops reading as a hue at all. Slot 6 read as dark grey, not as a colour.
 *  · #F59E0B (amber) against #4C9F38 (green) separated by only ΔE 6.9 under
 *    simulated protanopia — and they were ADJACENT slots, so roughly 8% of men
 *    could not tell series 4 from series 3 apart.
 *  · #F59E0B and #F0564A ARE `palette.warning` and `palette.alert`. Spending the
 *    semantic colours as categorical slots 4 and 5 means "amber = pending" and
 *    "coral = incident" stop being true the moment a chart has five series.
 *
 * ── SLOT 1 IS STILL BRAND TEAL, DELIBERATELY ──
 * Every single-series chart in the app reads slot 1 and nothing else (the
 * dashboard-home area chart takes `palette.iris` directly; the reports line
 * charts open on `series[0]`). Keeping #00A19A in slot 1 was a CONSTRAINT on the
 * replacement, not a happy accident — it is why those charts are pixel-identical
 * after this change and why nothing single-series had to be re-checked by eye.
 */
export const dataViz = {
  /* ── CATEGORICAL, light surface ──
   * teal (brand) → orange → blue → magenta → green → violet.
   * Worst ADJACENT pair: ΔE 10.6 under protanopia, 22.1 under normal vision.
   * All six clear 3:1 against `palette.surface`; the tightest is brand teal
   * itself at 3.20:1, so slot 1 is the one to watch if `surface` ever darkens.
   *
   * ── THE SERIES CAP — the rule most likely to be broken by the next person ──
   * Those numbers are ADJACENT-pair numbers: slot n against slot n+1. That is
   * the right measurement for a form where a reader compares neighbours — a line
   * chart with a legend, a grouped bar, a stacked bar. It is NOT the right
   * measurement for a form where every series is compared against every other.
   *
   * Under ALL-PAIRS validation this order holds to FOUR slots (worst 6.6 deutan
   * / 16.8 normal — the CVD number lands in the warn band, so four all-pairs
   * slots are legal only alongside a legend or direct labels). At FIVE it fails
   * hard: green↔orange collapses to ΔE 5.5 under deuteranopia and green↔teal to
   * 12.3 under normal vision, below the 15 floor.
   *
   * So a PIE, a DONUT or a 100%-STACKED BAR caps at FOUR categorical slots, and
   * a fifth category folds into "Other" with a drill-down. A line or bar chart
   * carrying a legend and direct labels may use all six.
   *
   * And take CONSECUTIVE slots. A three-series chart on 0/2/4 puts teal against
   * green, which is ΔE 12.3 under normal vision — under the floor. 0/1/2
   * measures 16.8.
   *
   * ── SEMANTIC TOKENS ARE RESERVED FOR STATE ──
   * `palette.warning`, `palette.alert` and `palette.success` encode a threshold
   * being breached. They always ship with a number or a label beside them, never
   * colour alone, and they must never be reached for as a series colour: the
   * moment amber means "series 4" it stops meaning "pending approval".
   *
   * ONE overlap survives, by value only: slot 5 is #4C9F38, which is also
   * `palette.success` (Leaf). It is the one hue already in the brand's
   * vocabulary that clears both the chroma floor and the CVD gate in that
   * position, so it stayed. The test pins the overlap set to exactly {success},
   * so a second one cannot creep in — if warning or alert reappears in `series`,
   * that test fails. */
  series: [
    '#00A19A', // 1 — brand teal (palette.teal). Unchanged; see above.
    '#E06A1B', // 2 — orange
    '#2A78D6', // 3 — blue
    '#B5479B', // 4 — magenta
    '#4C9F38', // 5 — green. Same value as palette.success; see the note above.
    '#7C4DBE', // 6 — violet
  ],

  /* ── CATEGORICAL, dark surface — UNUSED TODAY ──
   * This repo has NO dark mode: there is not one `prefers-color-scheme` query
   * and not one `data-theme` attribute anywhere in src. Nothing reads this array
   * and nothing should until a real dark chart surface token exists.
   *
   * It is here anyway because it was validated in the SAME pass as the light set
   * (dark band L 0.48–0.67, worst adjacent ΔE 10.6 deutan / 21.1 normal, all six
   * over 3:1 on a #1a1a19 ground, all-pairs holding to the same four slots).
   * Deriving a dark scale later and separately is how a light-mode teal gets
   * transplanted into a dark chart at the wrong lightness and quietly falls out
   * of the band — the failure the light set above was already shipping. Same hue
   * order as `series`, so a slot index means the same category in either mode. */
  seriesDark: [
    '#0FA69E', // 1 — brand teal, lifted for a dark ground
    '#DE7118', // 2 — orange
    '#3B82D8', // 3 — blue
    '#BA4F9F', // 4 — magenta
    '#4FA33D', // 5 — green
    '#8471D4', // 6 — violet
  ],

  /* ── SEQUENTIAL ramp — MAGNITUDE ONLY ──
   * One hue (OKLab hue spread 3°), lightness strictly monotonic 0.966 → 0.434.
   * For a heatmap cell, a choropleth fill, a density shade: anywhere the colour
   * means HOW MUCH. Never for identity — a ramp cannot say "this one is Vendor
   * and that one is Delivery", and splicing in a second hue to make it try turns
   * it into a rainbow, at which point the ordering it existed to show is gone.
   * It must stay one hue for the same reason it must stay monotonic.
   *
   * NOT a set of discrete swatches. The pale end is a wash: #EAF7F6 is 1.10:1
   * against `palette.surface` and only ΔL 0.051 from the step above it. That is
   * correct for a near-empty heatmap cell and wrong for a legend chip, a funnel
   * stage or a size tier — ordered CATEGORIES rendered as separate marks need
   * their own ramp with visible gaps (ΔL >= 0.06) and a light end over 2:1. */
  sequential: [
    '#EAF7F6',
    '#C9EBE8',
    '#9DDCD7',
    '#6BCAC3',
    '#35B3AA',
    '#00908A',
    '#005E59',
  ],

  /* ── DIVERGING ramp — only where a direction genuinely means better or worse ──
   * Coral end ← neutral → teal end. Legal for close rate against target,
   * occupancy against plan, SLA against commitment: cases where a reader must
   * see which side of the middle a value fell on.
   *
   * NOT for entry volume. More visitors is not good news, it is Tuesday, and
   * painting a busy day coral invents a judgement the data does not carry.
   *
   * THE MIDPOINT IS NEUTRAL GREY BY DESIGN. #DCE8E7 is `palette.line`, chroma
   * 0.013 — far under the 0.10 categorical floor, i.e. genuinely hueless. Give
   * the midpoint a hue and it reads as a third category, and the ramp stops
   * looking like a scale with a zero in the middle. */
  diverging: [
    '#B23127', // worse, far
    '#F0A79E', // worse, near
    '#DCE8E7', // neutral midpoint (palette.line)
    '#6BCAC3', // better, near
    '#007C76', // better, far
  ],

  /* Area gradient stops — the soft teal fade under a single-series line/area
   * chart. Brand teal at 28% down to fully transparent, so the fill reads as
   * atmosphere and never competes with the stroke. */
  areaFrom: 'rgba(0, 161, 154, 0.28)',
  areaTo: 'rgba(0, 161, 154, 0.00)',

  /* Chart furniture. Deliberately quieter than `palette.line` / `palette.slate`:
   * gridlines and axis labels are scaffolding, and at full strength they compete
   * with the marks they exist to measure. */
  grid: '#E4EDEC',
  /* Axis TICK LABELS are text and are read, unlike the axis line itself, so
     they follow the text floor rather than the 3:1 non-text one. Was #93A6A8
     (2.54:1) — the numbers under every chart in the product. */
  axis: '#5D7376',
} as const;

export const gradient = {
  /**
   * Aurora — RESERVED EXCLUSIVELY for AI features ("Lobbero IQ").
   * Using it elsewhere dilutes the AI signal. Matches lobbero_web's --aurora
   * exactly: Teal → Aurora end, at 100deg.
   */
  aurora: 'linear-gradient(100deg, #00A19A 0%, #43D6C9 100%)',
  auroraSoft:
    'linear-gradient(100deg, rgba(0,161,154,0.12) 0%, rgba(67,214,201,0.12) 100%)',

  /* Brand hero — login panel / marketing depth. Deep, layered lagoon. */
  brand: 'linear-gradient(158deg, #12B5AD 0%, #007C76 52%, #04413E 100%)',

  /* Primary button sheen (subtle top-light on teal).
   *
   * THESE STOPS CARRY WHITE TEXT, SO THEY ARE PICKED BY CONTRAST, NOT BY HUE.
   * The button label is 14px/600, which is NOT "large text" under WCAG, so it
   * needs 4.5:1. The old pair (#12B0A8 → #00A19A) measured 2.69:1 and 3.20:1
   * against white — the most-pressed control in the product had an unreadable
   * label in glare, which is the concierge's normal working condition.
   *
   * #00837D is 4.62:1 and #007C76 is 5.07:1. The lightest teal that clears the
   * floor at all is #00857F at 4.51:1 — too close to the line to ship, so the
   * ramp starts one step darker.
   *
   * palette.iris #00A19A is UNCHANGED and still correct for fills, borders,
   * icons and chart marks, where the applicable floor is SC 1.4.11's 3:1. */
  primary: 'linear-gradient(180deg, #00837D 0%, #007C76 100%)',
  primaryHover: 'linear-gradient(180deg, #007C76 0%, #005E59 100%)',
} as const;

/* ────────────────────────────  SPACING  ──────────────────────────── */
/* 8-POINT GRID — the layout law of the product.
 *
 * 8px is the base unit; 4px is the ONLY permitted half-step (icon gaps,
 * hairline insets). Every margin, padding, gap, row height, and control size
 * must resolve to a multiple of 8 (occasionally 4). No magic numbers. */

export const spacing = {
  none: '0',
  half: '4px', // 0.5× — sole sub-grid step
  xs: '8px', // 1×
  sm: '16px', // 2×
  md: '24px', // 3×
  lg: '32px', // 4×
  xl: '40px', // 5×
  xxl: '48px', // 6×
  xxxl: '64px', // 8×
  huge: '80px', // 10×
} as const;

/* space(n) → n × 8px. space(1)=8, space(2)=16, space(3)=24, space(4)=32… */
export const space = (n: number): string => `${n * 8}px`;

/* ─────────────────────────────  RADII  ───────────────────────────── */

export const radius = {
  xs: '8px',
  sm: '12px',
  md: '16px',
  lg: '20px',
  xl: '28px',
  pill: '999px',
  circle: '50%',
} as const;

/* ───────────────────────────  ELEVATION  ─────────────────────────── */
/* Soft, ink-tinted shadows — never harsh black. */

export const shadow = {
  /* Ink-tinted, never neutral black — ink is now #12292C (18, 41, 44). */
  xs: '0 1px 2px rgba(18, 41, 44, 0.05)',
  sm: '0 2px 8px rgba(18, 41, 44, 0.06)',
  md: '0 10px 28px rgba(18, 41, 44, 0.08)',
  lg: '0 20px 48px rgba(18, 41, 44, 0.12)',
  xl: '0 32px 80px rgba(18, 41, 44, 0.16)',
  /* Interaction rings
   *
   * OPAQUE, SINGLE-LAYER, AND MEASURED — because this is the ONLY focus
   * indicator the product has. It is paired with `outline: none` at ~200
   * sites, so if it is not visible, nothing is.
   *
   * The old ring was rgba(0,161,154,0.20), which composites to #CCECEB on white
   * — 1.25:1, failing SC 1.4.11's 3:1 for non-text. It was then replaced by a
   * two-layer ring (2px white spacer + 2px lagoon), which met contrast but
   * rendered as a DOUBLE BORDER on every input: teal border, white gap, teal
   * ring. Reported as a bug on login, onboarding and everywhere else.
   *
   * Now one opaque 2px lagoon ring flush against the control. #007C76 is
   * 5.5:1 on white and 5:1 on surfaceMuted, so it holds SC 1.4.11 on every
   * ground the app draws on without a spacer. On inputs it sits directly
   * against the focused 1px teal border and reads as ONE thicker border. */
  focus: '0 0 0 2px #007C76',
  focusError: '0 0 0 2px #B23127',
  /* Coloured glow under primary CTAs */
  brand: '0 12px 30px rgba(0, 161, 154, 0.30)',
  brandHover: '0 16px 40px rgba(0, 161, 154, 0.38)',

  /* THE TOP HIGHLIGHT ON A BRAND CHIP — the system's only inset shadow.
   *
   * A one-pixel white line along the top edge is what stops the logo mark and
   * the IQ surface reading as flat paint; it is a deliberate effect, not drift.
   * It was living as a raw `inset 0 1px 0 rgba(255,255,255,0.28)` at two sites
   * and rendering on 43 screens, which made it the most widespread untokenised
   * value in the product. Naming it is the fix — the effect stays exactly as it
   * was. */
  sheen: 'inset 0 1px 0 rgba(255, 255, 255, 0.28)',
  /* The same highlight on a white chip, where it has to work harder. */
  sheenStrong: 'inset 0 1px 0 rgba(255, 255, 255, 0.6)',
} as const;

/* ──────────────────────────  TYPOGRAPHY  ─────────────────────────── */

export const typography = {
  /* One family in-product keeps it clean. */
  ui: "'Inter', system-ui, -apple-system, 'Segoe UI', sans-serif",
  /* Display face — matches lobbero_web's --display so the wordmark and hero
   * headings are identical across the marketing site and the product.
   * ('Clash Display' was never actually loaded; Bricolage Grotesque is, via the
   * Google Fonts link in public/index.html.) */
  display:
    "'Bricolage Grotesque', 'Inter', system-ui, -apple-system, sans-serif",
  /* Monospace — lobbero_web --mono. Codes, tokens, log ids. */
  mono: "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace",

  weight: {
    light: 300,
    regular: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
    heavy: 800,
  },

  /* px scale — matches the numeric API used by the shared <Text> component. */
  size: {
    xxs: 12,
    xs: 13,
    sm: 14,
    base: 16,
    md: 18,
    lg: 20,
    xl: 24,
    xxl: 30,
    display: 40,
    displayLg: 56,
  },

  tracking: {
    tight: '-0.02em', // large display
    snug: '-0.011em', // headings / body default
    normal: '0',
    wide: '0.02em',
    caps: '0.08em', // uppercase eyebrows / labels
  },

  leading: {
    tight: 1.1,
    snug: 1.3,
    normal: 1.5,
    relaxed: 1.65,
  },
} as const;

/* ────────────────────────────  MOTION  ───────────────────────────── */
/* Smooth, expensive-feeling, never bouncy-cheap. */

export const easing = {
  standard: 'cubic-bezier(0.4, 0, 0.2, 1)',
  entrance: 'cubic-bezier(0.16, 1, 0.3, 1)', // decelerate — content arriving
  exit: 'cubic-bezier(0.4, 0, 1, 1)',
  spring: 'cubic-bezier(0.34, 1.4, 0.5, 1)', // gentle overshoot for press/pop
} as const;

export const duration = {
  instant: '90ms',
  fast: '160ms',
  base: '240ms',
  slow: '360ms',
  slower: '560ms',
} as const;

export const transition = {
  /* Colour / border / shadow only — cheap and always safe. */
  colors: `color ${duration.fast} ${easing.standard}, background-color ${duration.fast} ${easing.standard}, border-color ${duration.fast} ${easing.standard}, box-shadow ${duration.base} ${easing.standard}`,
  /* The general-purpose one, at the standard tempo.
   *
   * SCOPED, NOT `all`. This token used to read `all 240ms standard`, which put
   * `transition-property: all` on ~30 call sites and 390 rendered elements —
   * the exact thing §7 of design_system.md tells new code not to write, handed
   * out by the token that was meant to be the safe default. `all` animates
   * whatever happens to change, including layout properties, which is how a
   * hover ends up animating a width nobody meant to animate.
   *
   * The list below is every property these call sites actually move. Adding one
   * is a deliberate act; `all` was the absence of one. */
  base: `transform ${duration.base} ${easing.standard}, opacity ${duration.base} ${easing.standard}, box-shadow ${duration.base} ${easing.standard}, color ${duration.base} ${easing.standard}, background-color ${duration.base} ${easing.standard}, border-color ${duration.base} ${easing.standard}, fill ${duration.base} ${easing.standard}`,
  /* Transform with a touch of spring — for hovers/presses. */
  transform: `transform ${duration.base} ${easing.spring}`,
  /* Slow, luxurious — for large surfaces / hero elements. Same rule as `base`. */
  slow: `transform ${duration.slow} ${easing.entrance}, opacity ${duration.slow} ${easing.entrance}, box-shadow ${duration.slow} ${easing.entrance}, color ${duration.slow} ${easing.entrance}, background-color ${duration.slow} ${easing.entrance}, border-color ${duration.slow} ${easing.entrance}`,
} as const;

/* ─────────────────────────────  Z-INDEX  ─────────────────────────── */

export const zIndex = {
  base: 1,
  raised: 10,
  sticky: 100,
  dropdown: 1000,
  overlay: 2000,
  modal: 2100,
  /* One step above `modal`, for a dialog raised FROM INSIDE a modal — a delete
   * confirmation over a building or unit form. `overlay` (2000) painted it
   * underneath the very modal that opened it. Reserved for that case only: a
   * screen's own modal uses `modal`, never this. */
  modalTop: 2200,
  toast: 3000,
} as const;

/* ───────────────────────────  BREAKPOINTS  ───────────────────────── */
/* Mobile-first. Numeric values (px) for the `bp`/`up`/`down` helpers in
 * media-queries.tsx; string values kept for legacy callers. */

export const breakpoints = {
  smallMobile: '320px',
  mobile: '480px',
  phablet: '560px',
  tablet: '768px',
  laptop: '1024px',
  desktop: '1280px',
  largeDesktop: '1440px',
} as const;

export const bp = {
  /* 320 IS THE FLOOR (owner ruling R-18). The scale started at 360 while
   * design_system.md and R-10/S-1 already demanded no horizontal page scroll at
   * 320 — so the narrowest supported width was the one width with no name, and
   * every rule about it had to be written as a literal. Only the legacy string
   * map below carried it. Below 320 is not a target. */
  xxs: 320,
  xs: 360,
  sm: 480,
  md: 768, // tablet
  lg: 1024, // laptop
  xl: 1280, // desktop
  xxl: 1440,
} as const;

/* ────────────────────────────  LAYOUT  ───────────────────────────── */
/* Frame dimensions — all on the 8pt grid. Interactive elements must never
 * fall below `touchTarget` (44px) on touch devices. */

export const layout = {
  touchTarget: '44px', // minimum tap size
  navHeight: '72px', // top bar / logo rail height (mobile 64px)
  navHeightDesktop: '80px',
  sidebarWidth: '256px', // expanded dashboard rail
  sidebarCollapsed: '80px', // icon-only rail (tablet)
  contentMaxWidth: '1320px', // max readable content width
  gutter: '32px', // desktop page gutter
  gutterTablet: '24px',
  gutterMobile: '16px', // phone page gutter
  fieldHeight: '48px', // inputs / selects
  buttonHeight: '44px', // every normal application button
  controlHeight: '44px', // legacy alias for normal buttons / controls
  compactControlHeight: '32px', // pagination, segments, dense utilities only

  /* The widest a single item in the mobile bottom nav may be (WP-33).
   *
   * The bar is `overflow: hidden` and its strip is `white-space: nowrap` with no
   * cap at all, so one long destination name could take a third of a 390px bar
   * and push everything after it out of sight with no signal.
   *
   * IN `em`, NOT px, AND THAT IS THE WHOLE POINT. The cap means "about ten
   * characters of label" — a ceiling for the pathological case, never a routine
   * truncation. A px cap says that only at one font size: under the 200% text
   * zoom this work is verified at, a 160px cap would start cutting `Dashboard`
   * in half, which is the failure it exists to prevent. This is one of the few
   * places where a font-relative length is the correct token. */
  navItemMaxWidth: '10em',

  /* Horizontal space that any TOP-ANCHORED header must keep clear on its right
   * so the fixed NotificationDock never lands on the controls underneath it.
   *
   * The dock is `position: fixed` — it is not in any header's flow, so a header
   * laying out its own children edge-to-edge will happily put a profile avatar
   * or a sign-out button directly beneath the bell. Padding is the fix, and it
   * has to come from ONE number: a header that reserves its own guess drifts
   * the first time the dock's inset changes.
   *
   * 80px = touchTarget (44px, the bell's hit area)
   *      + spacing.md (24px, the dock's own right inset at >= md)
   *      + 12px clearance, which is what it takes to land on the grid.
   *
   * IT WAS 76px, AND 76 IS NOT A GRID VALUE. Every header that reserves it adds
   * its own gutter on top, so the reserve propagated as `padding-right: 108px`
   * (76 + 32), `100px` (76 + 24) and `92px` (76 + 16) across 26 screens — three
   * off-grid paddings produced by one off-grid token. At 80 the same sums are
   * 112 / 104 / 96, all multiples of 8, and the clearance grows by 4px.
   * The clearance is deliberately ONE step and not more: the profile avatar and
   * the bell read as a pair of controls in the same corner, and a wide gutter
   * between them reads as two unrelated things. 24px of clearance was tried and
   * looked like a mistake.
   * The dock's inset SHRINKS below md, so this one value stays sufficient at
   * every breakpoint instead of needing a responsive ladder of its own.
   *
   * Enforced by src/app-surfaces.test.ts, which does this arithmetic and fails
   * if the dock ever grows past what is reserved for it. */
  bellDockReserve: '80px',

  /* Vertical space a scrolling page must keep clear at its END so the fixed IQ
   * launcher never lands on the last control.
   *
   * The launcher (iq-dock.styled.tsx `Fab`) is 56px square at `bottom:
   * spacing.lg` — 88px of occupied corner — and it is `position: fixed`, so it
   * is in no page's flow and no page reserved anything for it. Measured on
   * /dashboard/buildings at 768: the launcher covered that row's "Actions"
   * button by 78% AND the page had NO SCROLL ROOM, so the control could not be
   * pressed at all — not "was awkward", could not be reached.
   *
   * 96px = 56 (launcher) + 32 (its bottom inset) + 8 (one clear step). On the
   * 8-point grid, and deliberately one step rather than a comfortable margin:
   * this is a floor that stops a control being buried, not a gutter. */
  fabReserve: '96px',
} as const;

/* Convenience aggregate. */
export const theme = {
  palette,
  status,
  dataViz,
  gradient,
  spacing,
  space,
  radius,
  shadow,
  typography,
  easing,
  duration,
  transition,
  zIndex,
  breakpoints,
  bp,
  layout,
} as const;

export type Theme = typeof theme;
export default theme;
